import React, { useEffect, useState } from "react";
import { COURSE_BASE_URL } from "../../services/courseService";
import Loading from "../../utils/Loading";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import Swal from "sweetalert2";
import { useAuthContext } from "../../hooks/useAuthContext";
import { getRoleDisplay } from "../../utils/getRoleDisplay";

const CourseTable = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courses, setCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const fetchCourseData = async () => {
    //use axios

    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${COURSE_BASE_URL}/api/courses/list`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch courses");
      }
      const data = await response.json();
      setCourses(data);
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }

    // try {
    //   setLoading(true);
    //   setError(null);
    //   const response = await fetch(`${COURSE_BASE_URL}/api/courses/list`);
    //   if (!response.ok) {
    //     throw new Error("Failed to fetch courses");
    //   }
    //   const data = await response.json();
    //   setCourses(data);
    // } catch (err) {
    //   setError(err.message || "Something went wrong");
    // } finally {
    //   setLoading(false);
    // }
  };

  const deleteCourse = async (e, _id) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      const result = await Swal.fire({
        title: "Confirm Deletion",
        text: "Are you sure you want to delete this course?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc3545",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
      });

      if (result.isConfirmed) {
        const response = await fetch(
          `${COURSE_BASE_URL}/api/courses/list/delete/${_id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to delete course");
        }

        Swal.fire({
          title: "Deleted!",
          text: "Course has been removed",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });

        fetchCourseData();
      }
    } catch (err) {
      Swal.fire({
        title: "Error!",
        text: err.message,
        icon: "error",
      });
    }
  };

  const filteredCourses = courses.filter((course) => {
    const matchesSearch =
      course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.instructor.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter =
      filter === "all" ||
      (filter === "free" && parseFloat(course.price) === 0) ||
      (filter === "paid" && parseFloat(course.price) > 0);
    return matchesSearch && matchesFilter;
  });

  useEffect(() => {
    fetchCourseData();
  }, []);

  // Calculate statistics for dashboard cards
  const totalCourses = courses.length;
  const freeCourses = courses.filter((c) => parseFloat(c.price) === 0).length;
  const paidCourses = totalCourses - freeCourses;
  const averageRating =
    courses.length > 0
      ? (
          courses.reduce((sum, c) => sum + (c.courseRating || 0), 0) /
          courses.length
        ).toFixed(1)
      : 0;

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <div>
          <h1 className="h2 mb-1">Course Management</h1>
          <p className="text-muted mb-0">
            View and manage all available courses
          </p>
        </div>
        {user?.isAdmin && getRoleDisplay() && (
          <Link
            to="/api/dashboard/add-course"
            className="btn btn-primary mt-2 mt-md-0"
          >
            <i className="fa fa-plus me-2"></i> Add New Course
          </Link>
        )}
      </div>

      {/* Dashboard Cards */}
      <div className="row mb-4">
        <div className="col-12 col-sm-6 col-xl-3 mb-4 mb-xl-0">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-2">Total Courses</h6>
                  <h3 className="mb-0">{totalCourses}</h3>
                </div>
                <div className="bg-primary bg-opacity-10 p-3 rounded">
                  <i className="fa fa-book text-primary"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-xl-3 mb-4 mb-xl-0">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-2">Free Courses</h6>
                  <h3 className="mb-0">{freeCourses}</h3>
                </div>
                <div className="bg-success bg-opacity-10 p-3 rounded">
                  <i className="fa fa-tag text-success"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-xl-3 mb-4 mb-sm-0">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-2">Paid Courses</h6>
                  <h3 className="mb-0">{paidCourses}</h3>
                </div>
                <div className="bg-info bg-opacity-10 p-3 rounded">
                  <i className="fa fa-dollar text-info"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-xl-3">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-2">Avg. Rating</h6>
                  <h3 className="mb-0">{averageRating}</h3>
                </div>
                <div className="bg-warning bg-opacity-10 p-3 rounded">
                  <i className="fa fa-star text-warning"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-8">
              <div className="input-group">
                <span className="input-group-text bg-transparent">
                  <i className="fa fa-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search courses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-4">
              <select
                className="form-select"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value="all">All Courses</option>
                <option value="free">Free Courses</option>
                <option value="paid">Paid Courses</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="card shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <Loading />
              <p className="mt-3">Loading courses...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger mx-4 mt-4">
              <i className="fa fa-exclamation-circle me-2"></i>
              {error}
              <button
                className="btn btn-sm btn-outline-danger ms-3"
                onClick={fetchCourseData}
              >
                Retry
              </button>
            </div>
          ) : filteredCourses.length === 0 ? (
            <div className="text-center py-5">
              <i className="fa fa-book-open fa-3x text-muted mb-3"></i>
              <h4>No courses found</h4>
              <p className="text-muted">
                {searchTerm || filter !== "all"
                  ? "Try adjusting your search criteria"
                  : "No courses available yet"}
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Course</th>
                    <th>Instructor</th>
                    <th>Price</th>
                    <th>Credits</th>
                    <th>Rating</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCourses.map((course) => (
                    <tr
                      key={course._id}
                      onClick={() =>
                        navigate(
                          `/api/dashboard/course-content-details/${course._id}`
                        )
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="bg-primary bg-opacity-10 text-primary p-2 rounded me-3">
                            <i className="fa fa-book"></i>
                          </div>
                          <div>
                            <h6 className="mb-0">{course.title}</h6>
                            <small className="text-muted">
                              {course.category}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>{course.instructor}</td>
                      <td>
                        {parseFloat(course.price) === 0 ? (
                          <span className="badge bg-success bg-opacity-10 text-success">
                            Free
                          </span>
                        ) : (
                          <span className="text-primary">
                            {parseFloat(course.price).toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="badge bg-info bg-opacity-10 text-info">
                          {course.credit}{" "}
                          {course.credit === 1 ? "credit" : "credits"}
                        </span>
                      </td>
                      <td>
                        {course.courseRating ? (
                          <span className="d-flex align-items-center">
                            <i className="fa fa-star text-warning me-1"></i>
                            {course.courseRating}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <small className="text-muted">
                          {format(new Date(course.createdAt), "MMM d, yyyy")}
                        </small>
                      </td>
                      <td>
                        <div className="d-flex">
                          <Link
                            to={`/api/dashboard/course-content-details/${course._id}`}
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <i className="fa fa-eye"></i>
                          </Link>
                          {user?.isAdmin && getRoleDisplay() && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={(e) => deleteCourse(e, course._id)}
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination would go here */}
      {!loading && !error && filteredCourses.length > 0 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="text-muted">
            Showing <span className="fw-bold">{filteredCourses.length}</span> of{" "}
            <span className="fw-bold">{filteredCourses.length}</span> courses
          </div>
          {/* Pagination controls would go here */}
        </div>
      )}
    </div>
  );
};

export default CourseTable;
